import { useEffect, useRef } from 'react';

import { getDocument, getWindow } from '@r-client/shared/util/core';

/**
 * Re-lands a deep link on `id` once the page has settled.
 *
 * The browser's own fragment scroll happens before the sections *above* the
 * target finish loading — the crypto rail swaps its skeleton for real tiles, and
 * the real-world-assets cards settle as the ticker resolves. Every pixel added
 * above the anchor pushes it down while the viewport stays put, so the reader
 * lands short of the heading, sometimes by a full card row. Scroll anchoring
 * doesn't cover fragment navigation, hence this correction.
 *
 * Runs at most once, so a later sort change can't yank the viewport, and no-ops
 * when the URL points somewhere else. It doesn't chase very late image loads —
 * observing for that would fight the reader's own scrolling.
 *
 * `location` is read in the effect only: the server has no fragment (browsers
 * don't send it), so reading it during render would be a hydration mismatch.
 */
export const useScrollToHashTarget = (id: string, isReady: boolean): void => {
  const hasScrolledRef = useRef(false);

  useEffect(() => {
    if (!isReady || hasScrolledRef.current) {
      return;
    }

    if (getWindow()?.location.hash.slice(1) !== id) {
      return;
    }

    // Set before the frame is requested, so a double-invoked effect can't scroll
    // twice.
    hasScrolledRef.current = true;

    getWindow()?.requestAnimationFrame(() => {
      // `behavior` left at its default: a deep link should land, not animate.
      getDocument()?.getElementById(id)?.scrollIntoView({ block: 'start' });
    });
  }, [id, isReady]);
};
